import {AxiosProgressEvent} from 'axios'
import {ApiException} from '@toolify/server/src/middleware/ApiResponseMiddleware/errors/ApiException'
import {useUploadSessionApiStore} from '@toolify/client/src/stores/api/UploadSessionApiStore/useUploadSessionApiStore'
import {uploadInParts} from '@toolify/client/src/modules/files/upload/uploadInParts'
import {UploadSessionMode} from '@toolify/client/src/modules/files/upload/types/UploadSessionMode'
import {IUploadSession} from '@toolify/client/src/modules/files/upload/types/IUploadSession'
import {IUploadFileOptions} from '@toolify/client/src/modules/files/upload/types/IUploadFileOptions'
import {IFileUploadLegacyContext} from '@toolify/client/src/modules/files/upload/types/IFileUploadLegacyContext'
import {IUploadInPartsResult} from '@toolify/client/src/modules/files/upload/types/IUploadInPartsResult'
import {getNativeFilePath} from '@toolify/client/src/plugins/FilePickerPlugin'
import {UploadSessionStatus} from '@toolify/server/src/models/mongoose/UploadSessionModel/enum/UploadSessionStatus'
import {
  buildUploadTargetKey,
  findResumableUpload,
  forgetResumableUpload,
  rememberResumableUpload,
} from '@toolify/client/src/modules/files/upload/uploadResumeStore'
import {
  IUploadDebugSession,
  createUploadDebugSession,
  formatBytes,
  formatThroughput,
  reportUploadInputProblems,
  reportUploadSessionPlanProblems,
} from '@toolify/client/src/modules/files/upload/uploadDebugLog'
import {isPolicyLimitExceededError} from '@toolify/client/src/modules/billing/helpers/getPolicyLimitExceededToastMessage'
import {showPolicyLimitExceededToast} from '@toolify/client/src/modules/billing/helpers/showPolicyLimitExceededToast'
import {KnownError} from '@toolify/server/src/middleware/ApiResponseMiddleware/types/KnownError'
import {FileUploadTargetType} from '@toolify/client/src/modules/files/upload/types/FileUploadTargetType'
import {useMessages} from '@toolify/client/src/modules/notifications/composables/useMessages'
import {i18n} from '@toolify/client/src/setup/i18n'

const DEFAULT_UPLOAD_MIME_TYPE = 'application/octet-stream'
const COMPLETE_RETRY_DELAYS_IN_MS = [1000, 3000, 8000]

function delay(durationInMs: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, durationInMs))
}

// once the parts are in storage the only safe recovery is to complete the same session again -
// complete is replay-safe server-side, while re-uploading would create a second file
function isRetryableCompleteError(error: unknown): boolean {
  if(error instanceof ApiException) {
    return error.httpCode >= 500
  }
  return true
}

function isFilesCellBeingCopiedError(error: unknown, targetType: FileUploadTargetType): error is ApiException {
  return targetType === FileUploadTargetType.DatabaseItemCellFile
    && error instanceof ApiException
    && error.name === KnownError.Conflict
}

function describeError(error: unknown): Record<string, unknown> {
  if(error instanceof ApiException) {
    return {
      kind: 'ApiException',
      name: error.name,
      httpCode: error.httpCode,
      message: error.message,
    }
  }
  return {
    kind: (error as Error)?.name || typeof error,
    message: (error as Error)?.message ?? String(error),
  }
}

export function useFileUpload() {
  const uploadSessionApiStore = useUploadSessionApiStore()
  const messages = useMessages()

  function showFilesCellBeingCopiedToast(error: unknown, targetType: FileUploadTargetType): void {
    if(isFilesCellBeingCopiedError(error, targetType)) {
      messages.warning(i18n.global.t('files.copyingFilesHint'), {replaceKey: 'files.copyingFiles'})
    }
  }

  // an interrupted upload (closed tab, power cut) leaves a live session holding the parts that already landed;
  // re-picking the same file for the same target lets us carry on instead of re-sending everything
  async function resumeDirectUploadSession<TResult, TCompleteResponse>(
    options: IUploadFileOptions<TResult, TCompleteResponse>,
    debugSession: IUploadDebugSession,
  ): Promise<{uploadSession: IUploadSession, alreadyUploadedPartNumbers: number[]}|null> {
    const {targetType, target, file, signal} = options
    const record = findResumableUpload(file, targetType, target)
    if(!record) {
      return null
    }
    try {
      const state = await uploadSessionApiStore.getUploadSession(record.uploadSessionId, signal)
      if(state.status !== UploadSessionStatus.Created || !state.partCount) {
        forgetResumableUpload(record.uploadSessionId)
        debugSession.log('resume: dropped', {reason: `session status is "${state.status}"`})
        return null
      }
      if(Math.ceil(file.size / state.partSize) !== state.partCount) {
        forgetResumableUpload(record.uploadSessionId)
        debugSession.warn('resume: dropped, the stored part plan does not fit this file')
        return null
      }
      debugSession.log('resume: reusing session', {
        id: state.id,
        alreadyUploaded: state.uploadedPartNumbers.length,
        of: state.partCount,
      })
      return {
        uploadSession: {
          id: state.id,
          fileId: null,
          mode: UploadSessionMode.DirectMultipart,
          partSize: state.partSize,
          partCount: state.partCount,
          parts: [],
          expiresAt: null,
        },
        alreadyUploadedPartNumbers: state.uploadedPartNumbers,
      }
    } catch(error) {
      if(signal?.aborted) {
        throw error
      }
      forgetResumableUpload(record.uploadSessionId)
      debugSession.log('resume: dropped, the session is gone', describeError(error))
      return null
    }
  }

  async function createDirectUploadSession<TResult, TCompleteResponse>(
    options: IUploadFileOptions<TResult, TCompleteResponse>,
    debugSession: IUploadDebugSession,
  ): Promise<IUploadSession|null> {
    const {targetType, target, file, name, signal} = options
    // a natively picked File is a zero-byte handle whose bytes live on disk, so slicing it would upload empty parts
    if(getNativeFilePath(file)) {
      debugSession.log('path: legacy', {reason: 'natively picked file, bytes are not in the blob'})
      return null
    }
    const sessionStartedAtInMs = performance.now()
    try {
      const uploadSession = await uploadSessionApiStore.createUploadSession({
        targetType,
        target,
        name: name || file.name,
        mimeType: file.type || DEFAULT_UPLOAD_MIME_TYPE,
        size: file.size,
      }, signal)
      debugSession.log('session: created', {
        id: uploadSession.id,
        mode: uploadSession.mode,
        fileId: uploadSession.fileId,
        durationInMs: Math.round(performance.now() - sessionStartedAtInMs),
      })
      if(uploadSession.mode !== UploadSessionMode.DirectMultipart) {
        debugSession.log('path: legacy', {reason: `server chose mode "${uploadSession.mode}"`})
        return null
      }
      reportUploadSessionPlanProblems(debugSession, {
        partSize: uploadSession.partSize,
        partCount: uploadSession.partCount,
        expiresAt: uploadSession.expiresAt,
        fileSize: file.size,
        signedPartCount: (uploadSession.parts || []).length,
      })
      rememberResumableUpload({
        uploadSessionId: uploadSession.id,
        targetKey: buildUploadTargetKey(targetType, target),
        fileName: file.name,
        fileSize: file.size,
        fileLastModified: file.lastModified,
        createdAtMs: Date.now(),
      })
      debugSession.log('path: direct')
      return uploadSession
    } catch(error) {
      if(signal?.aborted) {
        throw error
      }
      if(isPolicyLimitExceededError(error)) {
        debugSession.warn('session: storage limit exceeded, not falling back to the proxy', describeError(error))
        showPolicyLimitExceededToast(error)
        throw error
      }
      if(isFilesCellBeingCopiedError(error, targetType)) {
        debugSession.warn('session: files are still being copied to the cell, not falling back to the proxy', describeError(error))
        showFilesCellBeingCopiedToast(error, targetType)
        throw error
      }
      debugSession.warn('path: legacy, creating the session failed', describeError(error))
      return null
    }
  }

  async function transferParts<TResult, TCompleteResponse>(
    uploadSession: IUploadSession,
    options: IUploadFileOptions<TResult, TCompleteResponse>,
    onProgress: (progressEvent: AxiosProgressEvent) => void,
    debugSession: IUploadDebugSession,
    alreadyUploadedPartNumbers?: number[],
  ): Promise<IUploadInPartsResult> {
    const {file, signal, pauseController} = options
    return await uploadInParts({
      file,
      session: uploadSession,
      signMoreParts: (partNumbers) => {
        return uploadSessionApiStore.signUploadSessionParts(uploadSession.id, partNumbers, signal)
      },
      onProgress,
      signal,
      debugSession,
      pauseController,
      alreadyUploadedPartNumbers,
    })
  }

  async function completeUploadSession<TCompleteResponse>(
    uploadSession: IUploadSession,
    debugSession: IUploadDebugSession,
    signal?: AbortSignal,
  ): Promise<TCompleteResponse> {
    let attempt = 0
    const completeStartedAtInMs = performance.now()
    for(;;) {
      try {
        const completeResponse = await uploadSessionApiStore.completeUploadSession(uploadSession.id, signal)
        debugSession.log('complete: ok', {
          durationInMs: Math.round(performance.now() - completeStartedAtInMs),
          attempts: attempt + 1,
          responseKeys: Object.keys((completeResponse ?? {}) as Record<string, unknown>),
        })
        if(!completeResponse) {
          debugSession.warn('complete: server returned an empty body, the call site has nothing to map')
        }
        return completeResponse as TCompleteResponse
      } catch(error) {
        if(signal?.aborted || !isRetryableCompleteError(error) || attempt >= COMPLETE_RETRY_DELAYS_IN_MS.length) {
          debugSession.warn('complete: failed', {
            attempts: attempt + 1,
            ...describeError(error),
          })
          if(isPolicyLimitExceededError(error)) {
            showPolicyLimitExceededToast(error)
          }
          throw error
        }
        debugSession.warn('complete: retrying', {
          attempt: attempt + 1,
          delayInMs: COMPLETE_RETRY_DELAYS_IN_MS[attempt],
          ...describeError(error),
        })
        await delay(COMPLETE_RETRY_DELAYS_IN_MS[attempt])
        attempt++
      }
    }
  }

  // the abort request must never carry the upload's signal - it is the cancellation itself that has to reach the server
  async function abortUploadSessionQuietly(uploadSessionId: string, debugSession: IUploadDebugSession): Promise<void> {
    await uploadSessionApiStore.abortUploadSession(uploadSessionId)
      .then(() => debugSession.log('session: aborted on the server'))
      .catch((error) => debugSession.warn('session: abort request failed', describeError(error)))
  }

  async function uploadFile<TResult, TCompleteResponse = TResult>(
    options: IUploadFileOptions<TResult, TCompleteResponse>,
  ): Promise<TResult> {
    const {legacyUpload, mapCompleteResponse, signal, file, targetType, target} = options
    const debugSession = createUploadDebugSession(targetType)
    debugSession.log('start', {
      name: file.name,
      size: formatBytes(file.size),
      sizeInBytes: file.size,
      mimeType: file.type || `(none) -> ${DEFAULT_UPLOAD_MIME_TYPE}`,
      target,
    })
    reportUploadInputProblems(debugSession, file, targetType, target)
    const onProgress = (progressEvent: AxiosProgressEvent) => {
      options.onProgress?.(progressEvent)
    }
    const legacyContext: IFileUploadLegacyContext = {
      onProgress,
      signal,
    }
    const runLegacyUpload = async (reason: string): Promise<TResult> => {
      const legacyStartedAtInMs = performance.now()
      try {
        const result = await legacyUpload(legacyContext)
        const durationInMs = Math.round(performance.now() - legacyStartedAtInMs)
        debugSession.log('done via legacy proxy', {
          reason,
          durationInMs,
          throughput: formatThroughput(file.size, durationInMs),
          totalInMs: debugSession.elapsedInMs(),
        })
        return result
      } catch(error) {
        debugSession.warn('legacy proxy upload failed', {
          reason,
          durationInMs: Math.round(performance.now() - legacyStartedAtInMs),
          ...describeError(error),
        })
        showFilesCellBeingCopiedToast(error, targetType)
        throw error
      }
    }

    const resumed = await resumeDirectUploadSession(options, debugSession)
    const uploadSession = resumed?.uploadSession ?? await createDirectUploadSession(options, debugSession)
    if(!uploadSession) {
      return await runLegacyUpload('direct upload unavailable')
    }
    // only a part-addressed transfer can be paused, so the control becomes available exactly here
    options.pauseController?.markSupported(true)
    let transferResult: IUploadInPartsResult
    try {
      transferResult = await transferParts(
        uploadSession,
        options,
        onProgress,
        debugSession,
        resumed?.alreadyUploadedPartNumbers,
      )
    } catch(error) {
      options.pauseController?.markSupported(false)
      // an explicit cancel means the user is done with this upload, so it is torn down rather than kept
      // for resume; resume exists for the interruptions that never get to run any code at all
      forgetResumableUpload(uploadSession.id)
      await abortUploadSessionQuietly(uploadSession.id, debugSession)
      if(signal?.aborted) {
        debugSession.log('cancelled by the user during transfer')
        throw error
      }
      return await runLegacyUpload('part transfer failed')
    }
    options.pauseController?.markSupported(false)
    let completeResponse: TCompleteResponse
    try {
      completeResponse = await completeUploadSession<TCompleteResponse>(uploadSession, debugSession, signal)
    } catch(error) {
      // the bytes are already in storage, so re-uploading through the proxy would create a second file
      debugSession.warn('not falling back to the proxy: the object is already in storage')
      showFilesCellBeingCopiedToast(error, targetType)
      forgetResumableUpload(uploadSession.id)
      await abortUploadSessionQuietly(uploadSession.id, debugSession)
      throw error
    }
    forgetResumableUpload(uploadSession.id)
    const totalInMs = debugSession.elapsedInMs()
    debugSession.log('done via direct upload', {
      totalInMs,
      bytesSent: formatBytes(transferResult.transferredBytes),
      throughput: formatThroughput(transferResult.transferredBytes, totalInMs),
    })
    return mapCompleteResponse
      ? mapCompleteResponse(completeResponse)
      : completeResponse as unknown as TResult
  }

  return {
    uploadFile,
  }
}
